import { useQuery } from '@tanstack/react-query';

import { getMyPermissions, type AssetLibraryPermissions } from './api';

/**
 * What the caller may do in Asset Library, from `GET /me/permissions`.
 *
 * Gate on these flags, never on the role name — the same contract as Content
 * Tracker's `usePermissions`, but against Asset Library's own matrix. Reading
 * Content Tracker's permissions here was how an asset manager (not an admin in
 * Content Tracker) ended up locked out of edits the backend allowed.
 *
 * Its own query key rather than `['user-permissions']`: the two endpoints
 * answer different questions, and sharing a key would let whichever loaded
 * last overwrite the other. User management removes this key when it saves a
 * user, so a role change is never answered from the cache.
 *
 * `isReady`, not `isFetching`, is what callers wait on: a background refetch
 * of permissions that are already known must not blank the page.
 */
export const ASSET_LIBRARY_PERMISSIONS_QUERY_KEY = ['asset-library', 'permissions'] as const;

/**
 * Every flag off. Used until the real answer arrives and if it never does, so
 * a slow or failed request shows fewer controls rather than ones the backend
 * will refuse.
 */
export const NO_PERMISSIONS: AssetLibraryPermissions = {
  can_view_staged: false,
  can_view_source_images: false,
  can_manage_assets: false,
  can_delete_assets: false,
  can_edit_published: false,
  can_brief: false,
  can_manage_all_briefs: false,
  can_view_all_briefs: false,
  can_view_market_briefs: false,
  can_download_assets: false,
  role: '',
};

export function useAssetLibraryPermissions({
  enabled = true,
}: {
  /**
   * Off for callers that may render for users without Asset Library at all
   * (the suite nav): the endpoint refuses them, and asking is wasted.
   */
  enabled?: boolean;
} = {}): {
  permissions: AssetLibraryPermissions;
  /**
   * The real permissions are in. Until then `permissions` is `NO_PERMISSIONS`,
   * which is safe to render but is not an answer: anything that *decides*
   * from the permissions (which tab, whether a shared link's row survives)
   * waits for this.
   */
  isReady: boolean;
  /**
   * The request failed and there is nothing cached to fall back on. Without
   * this a failure is indistinguishable from a role that may do nothing, and
   * an admin is shown the viewer UI with no explanation.
   */
  isError: boolean;
  refetch: () => void;
} {
  const { data, isError, refetch } = useQuery({
    queryKey: ASSET_LIBRARY_PERMISSIONS_QUERY_KEY,
    queryFn: getMyPermissions,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // A 401/403 is an answer, not a blip: retrying only delays the page.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });

  // Keyed on `data`, not on the query status: a failed background refetch
  // keeps the last good answer, and that answer is still the one to use.
  return {
    permissions: data ?? NO_PERMISSIONS,
    isReady: data !== undefined,
    isError: isError && data === undefined,
    refetch: () => void refetch(),
  };
}
